import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { fromEvent, map, startWith, Subscription } from "rxjs";

import { allThematics } from "../../../../contents/models/contents.entity";
import { ContentsRepository } from "../../../../contents/repositories/contents.repository";
import { contentThematicCategoryTranslation } from "../../../../contents/translate";
import { mediumCategoryColors } from "../../../../thematics/colors";
import { ThematicCategorySlug } from "../../../../thematics/models/category.model";
import { LocaleService } from "../../../services/locale.service";
import { capitalize } from "../../../tool-functions/capitalize";
import { extractVideoIdFromYoutubeUrl } from "../../../tool-functions/video";
import { IconType } from "../../icon/icon.component";


@Component({
    selector: 'app-media-card',
    templateUrl: './media-card.component.html',
    styleUrls: [ './media-card.component.scss' ]
})

export class MediaCardComponent implements OnInit, OnDestroy {
    @Input() content: any;

    selectedCategory: any;

    isSmallScreen: boolean = false;

    resizeSubscription: Subscription = new Subscription();

    @Output() favoriteChanged = new EventEmitter<{contentId: string, isFavorited: boolean}>();
    
    constructor(
        private sanitizer: DomSanitizer,
        private readonly router: Router, 
        private readonly contentsRepository: ContentsRepository,
        private readonly localeService: LocaleService) { }

    ngOnInit(): void {
        this.resizeSubscription = fromEvent(window, 'resize')
        .pipe(
          startWith(this.getScreenWidth),
          map(() => window.innerWidth < 801)
        )
        .subscribe(isSmall => {
            this.isSmallScreen = isSmall;
        });
    }

    ngOnDestroy(): void {
        this.resizeSubscription.unsubscribe();
    }

    get associatedThematics() {
        const thematicsTitles = this.content.thematics.map((thematic: any) => {
          const key = allThematics.find((t: any) => t.key === thematic.slug)?.key;
          return key ? this.localeService.instant(`resources.themes.${key}`) : (thematic.name || '');
        });
        return thematicsTitles.join(', ');
    }

    get remainingThematicslength() {
        return this.content.thematics.length - 2;
    }

    public youtubeUrl(url: string) {
        const link = url.includes('s3') ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${extractVideoIdFromYoutubeUrl(url)}`);
        return link;
    }

    get isVideo() {
        return ['video', 'podcast'].includes(this.content.format);
    }

    public getIcon(): IconType {
        if(this.content.format === 'pratical_sheet'){
            return 'pdf';
        }

        if(this.content.format === 'test'){
            return 'file';
        }

        return 'file';
    }

    getPillarIcon(pillar: any): IconType {
        if(pillar.name === 'relations' || pillar.name === 'environment'){
            return 'house-rustic';
        }

        if(pillar.name === 'posture' || pillar.name === 'event'){
            return 'storm';
        }

        return 'candle';
    }

    handleNoVideoClick(event: any) {
        event.stopPropagation();
        window.open(this.content.resource, '_blank');
    }

    goToContent() {
        (window._mtm || []).push({ 'event': 'clic-ressource', 'nom-ressource': this.content.title });
        this.router.navigate([`/resources/all/${this.content.id}`]);
    }

    getCategoryNameBySlug(slug: ThematicCategorySlug): string {
        const key = `resources.categories.${slug}`;
        const translated = this.localeService.instant(key);
        if (translated && translated !== key) {
          return translated;
        }
        const fallback = (contentThematicCategoryTranslation as Record<string, string>)[slug];
        return capitalize(fallback || '') || '';
    }

    getColorBySlug(slug: ThematicCategorySlug): any {
        return mediumCategoryColors[slug] || '';
    }

    onSelectCategory(event: any, category: any): void {
        if(this.isSmallScreen) {
            event.stopPropagation();
            this.selectedCategory = category;
        }
    }

    capitalizeName(name: string): string {
        return capitalize(name);
    }

    isNew(): boolean {
        if (!this.content?.createdAt) return false;
        const createdAt = new Date(this.content.createdAt);
        const now = new Date();
        const diffInMs = now.getTime() - createdAt.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays < 30;
    }

    updateFavorite(event: any) {
        event.stopPropagation();
    
        if (!this.content?.id) return;
        
        if (this.content.isFavorited) {
          this.contentsRepository.removeFavorite(this.content.id).subscribe({
            next: () => {
              this.content.isFavorited = false;
              this.favoriteChanged.emit({
                contentId: this.content.id,
                isFavorited: false
              });
            },
            error: (err) => console.error('Error removing favorite:', err)
          });
        } else {
          this.contentsRepository.addFavorite(this.content.id).subscribe({
            next: () => {
              this.content.isFavorited = true;
            },
            error: (err) => console.error('Error adding favorite:', err)
          });
        }
    }

    get getScreenWidth() {
        return window.innerWidth;
    }
}